import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, retry, shareReplay, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ResponseEnvelope } from '../models/response-envelope';

/** Generic client for the per-user key/value settings store (`/v2/userSettings`).
 *  Unknown keys are accepted opaquely by the backend, so FE-owned preferences need
 *  no server registration. Bearer-authed via the HTTP interceptor. */
@Injectable({ providedIn: 'root' })
export class UserSettingsService {
  private base = `${environment.apiUrl}/v2/userSettings`;
  // The settings blob is fetched once and shared: on every authenticated transition the
  // three root stores (Number/Date/ReleaseNotes) each call getAll() in the same tick, which
  // without this cache fired three identical GETs for the same blob. shareReplay lets those
  // near-simultaneous subscribers share ONE request. Invalidated on any write (below) and on
  // logout (UserSettingStore.init), so a stale or cross-user blob is never served.
  private cached$?: Observable<Record<string, unknown>>;

  // Cached shared load for once-per-login consumers (see getAllShared()).
  private shared?: Observable<Record<string, unknown>>;
  // Identity the cached load reflects (the userId passed by consumers). Kept so a cross-tab identity
  // switch A→B rebuilds the cache exactly ONCE: the first consumer to call getAllShared(B) sees the key
  // change and re-fetches; the second sees the key already at B and reuses the shared observable. Without
  // this, each consumer had to invalidateShared() before loading, and the second consumer's invalidate
  // dropped the first's freshly-built cache — firing two identical GET /v2/userSettings on every switch.
  private sharedKey?: unknown;
  // A transient failure on the once-per-login load must not strand the whole session on default
  // settings, so getAllShared retries a few times before giving up (see below).
  private static readonly SHARED_RETRY_COUNT = 2;
  private static readonly SHARED_RETRY_DELAY_MS = 300;

  constructor(private http: HttpClient) {}

  getAll(): Observable<Record<string, unknown>> {
    if (!this.cached$) {
      // Cache only the SUCCESS value: shareReplay sits above catchError so a completed
      // response is shared by the near-simultaneous stores, but an error terminates this
      // inner stream instead of being buffered as a value.
      this.cached$ = this.http
        .get<ResponseEnvelope<Record<string, unknown>>>(this.base, { withCredentials: true })
        .pipe(
          map(res => res.data ?? {}),
          shareReplay({ bufferSize: 1, refCount: false })
        );
    }
    // Capture THIS observable so the error handler only clears the failed one. Between now and the
    // error firing, a write's invalidate()+a fresh getAll() may have already replaced this.cached$
    // with a new, successful blob; a bare invalidate() there would wipe that good cache and force a
    // needless re-fetch. Clear only if the field still points at the stream that failed.
    const attempt = this.cached$;
    // Treat a failed load as "no settings" so a transient error can't silently break consumers
    // that combineLatest on this (e.g. the login-modal prompt would never fire). Crucially, DROP
    // the cache on error so the NEXT getAll() retries — otherwise one transient blip at login
    // would poison the shared blob for the whole session and every store (Number/Date/Release)
    // would read empty settings until logout, ignoring the user's saved preferences.
    return attempt.pipe(
      catchError(() => {
        if (this.cached$ === attempt) this.invalidate();
        return of({} as Record<string, unknown>);
      })
    );
  }

  /** Drop the cached settings blob so the next getAll() re-fetches. Called after any write
   *  (so a subsequent read sees it) and on logout (so the next user re-fetches their own). */
  invalidate(): void {
    this.cached$ = undefined;
  }

  /**
   * De-duplicated load of the whole settings blob for app-wide consumers that fetch ONCE per login.
   * DateFormatService and ReleaseNotesStateService both react to the same `selectIsFullyAuthenticated`
   * false→true transition; calling {@link getAll} from each would issue two identical
   * `GET /v2/userSettings` on every login. `shareReplay(1)` collapses those concurrent (and any repeat)
   * subscribers onto a SINGLE request whose response is replayed.
   *
   * Pass `cacheKey` (the current userId) so a cross-tab identity switch A→B rebuilds the cache exactly
   * ONCE: whichever consumer calls first sees the key change from A to B and re-fetches; the other sees
   * the key already at B and reuses the shared observable. Consumers therefore MUST NOT invalidateShared()
   * before loading on a switch — that dropped the freshly-built cache and caused a second identical fetch.
   * Omit `cacheKey` for a keyless shared load (all callers dedupe onto one request until invalidated).
   *
   * The cache is still dropped on logout so the SAME user logging back in re-fetches (the key alone
   * wouldn't change) — call {@link invalidateShared} from each consumer's logout handler. It is
   * idempotent, so as long as ANY once-per-login consumer invalidates on logout the cache is cleared.
   *
   * Built from the RAW request rather than {@link getAll} on purpose: getAll swallows a failure to `{}`,
   * so caching IT would bake a transient early-login blip into the replayed value and pin the user on
   * default settings for the WHOLE session (no retry until logout). Instead we retry a few times, and on
   * a FINAL failure emit `{}` for the current subscribers AND drop the cache (`this.shared = undefined`)
   * so a later call re-fetches instead of replaying the failure.
   */
  getAllShared(cacheKey?: unknown): Observable<Record<string, unknown>> {
    // Restore the swallow-to-{} contract on top of the shared raw load, for consumers that don't need to
    // distinguish "load failed" from "user has no settings" (e.g. ReleaseNotesStateService's combineLatest,
    // which must not error). getAllSharedStrict() surfaces the failure for consumers that DO care.
    return this.getSharedRaw(cacheKey).pipe(catchError(() => of({} as Record<string, unknown>)));
  }

  /**
   * Like {@link getAllShared} but SURFACES a final load failure (after the internal retries) through the
   * error channel instead of swallowing it to `{}`. Shares the EXACT same single request as getAllShared
   * (both wrap one {@link getSharedRaw}), so pairing the two once-per-login consumers still fires only ONE
   * GET. Use this when the caller must tell "the load failed" apart from "the user genuinely has no
   * settings": DateFormatService re-arms its once-per-identity guard on failure so a transient
   * /v2/userSettings outage during the post-login window doesn't pin the whole session on default formats.
   */
  getAllSharedStrict(cacheKey?: unknown): Observable<Record<string, unknown>> {
    return this.getSharedRaw(cacheKey);
  }

  /**
   * The single cached load underpinning {@link getAllShared} and {@link getAllSharedStrict}. Retries a
   * transient failure a few times, and on a FINAL failure drops the cache (so a later call re-fetches
   * instead of replaying the failure) AND rethrows — the swallow-to-{} lives in getAllShared so the two
   * variants can share this one request while offering different failure semantics.
   */
  private getSharedRaw(cacheKey?: unknown): Observable<Record<string, unknown>> {
    if (!this.shared || this.sharedKey !== cacheKey) {
      this.sharedKey = cacheKey;
      this.shared = this.http
        .get<ResponseEnvelope<Record<string, unknown>>>(this.base, { withCredentials: true })
        .pipe(
          map(res => res.data ?? {}),
          retry({ count: UserSettingsService.SHARED_RETRY_COUNT, delay: UserSettingsService.SHARED_RETRY_DELAY_MS }),
          catchError((err) => {
            // Only drop the cache if a newer load for a DIFFERENT key hasn't already replaced it, so a
            // late failure from a superseded fetch can't wipe the current user's in-flight load.
            if (this.sharedKey === cacheKey) {
              this.shared = undefined;
              this.sharedKey = undefined;
            }
            return throwError(() => err);
          }),
          shareReplay({ bufferSize: 1, refCount: false }),
        );
    }
    return this.shared;
  }

  /** Drop the {@link getAllShared} cache so the next call re-fetches (call on logout). */
  invalidateShared(): void {
    this.shared = undefined;
    this.sharedKey = undefined;
  }

  set(key: string, value: unknown): Observable<unknown> {
    // JSON-encode the body explicitly: Angular sends a raw string body as `text/plain`
    // WITHOUT quoting it, so a value like `2026.06.0` reaches the backend's Jackson
    // `@RequestBody` parser as an invalid JSON token and 500s. JSON.stringify makes every
    // value type (string, boolean, number) a valid JSON document.
    return this.http
      .put<ResponseEnvelope<unknown>>(`${this.base}/${key}`, JSON.stringify(value), {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
      })
      .pipe(
        tap(() => this.invalidate()),
        map(res => res.data)
      );
  }

  reset(key: string): Observable<void> {
    return this.http
      .delete<ResponseEnvelope<null>>(`${this.base}/${key}`, { withCredentials: true })
      .pipe(
        tap(() => this.invalidate()),
        map(() => undefined)
      );
  }
}
